import { inject, Injectable, Injector } from '@angular/core';

import { map, switchMap } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { NotificationsController } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import { AskTemplatesDataService } from '@tellent-ask-hr/templates-logic';

import { AskTemplatesDialogService } from './ask-templates-dialog.service';

@Injectable({ providedIn: 'root' })
export class AskTemplateNotificationsService {
  private readonly i18n = inject(I18nService);
  private readonly notificationsController = inject(NotificationsController);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly askTemplatesDialogService = inject(AskTemplatesDialogService);
  private readonly askTemplatesDataService = inject(AskTemplatesDataService);
  private readonly injector = inject(Injector);

  constructor() {
    this.watchEvents();
  }

  private watchEvents() {
    this.watchOnCreateTemplate();
    this.watchOnUpdateTemplate();
    this.watchDuplicateTemplate();
    this.watchDeleteTemplate();
  }

  private watchOnCreateTemplate() {
    this.askTemplatesDataService.templateCreated$
      .pipe(this.untilDestroyed)
      .subscribe(({ payload }) => {
        const title = this.i18n.translate(
          payload.data.active
            ? 'tlc.ask_hr.forms.notifications.template_created_active'
            : 'tlc.ask_hr.forms.notifications.template_created',
        );
        this.notificationsController.success({ title });
      });
  }

  private watchOnUpdateTemplate() {
    this.askTemplatesDataService.templateUpdated$.pipe(this.untilDestroyed).subscribe(() => {
      const title = this.i18n.translate('tlc.ask_hr.forms.notifications.changes_saved');
      this.notificationsController.success({ title });
    });
  }

  private watchDuplicateTemplate() {
    this.askTemplatesDataService.templateDuplicated$
      .pipe(
        switchMap(({ payload }) => {
          const title = this.i18n.translate('tlc.ask_hr.forms.notifications.form_duplicated');
          return this.notificationsController
            .success({
              title,
              topButtonLabel: this.i18n.translate('tlc.ask_hr.forms.notifications.view_form'),
            })
            .topButtonClick()
            .pipe(map(() => payload.data.id));
        }),
        this.untilDestroyed,
      )
      .subscribe(id => {
        this.askTemplatesDialogService.openEditTemplateDialog(this.injector, {
          id,
          firstView: 'form',
        });
      });
  }

  private watchDeleteTemplate() {
    this.askTemplatesDataService.templateDeleted$.pipe(this.untilDestroyed).subscribe(() => {
      const title = this.i18n.translate('tlc.ask_hr.forms.notifications.form_deleted');
      this.notificationsController.success({ title });
    });
  }
}
